import type { RefObject } from 'react';
import type ReactPlayer from 'react-player';

import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import {
    isRetryStillWanted,
    mediaErrorAction,
} from '/@/renderer/features/player/audio-player/playback-guards';
import { AudioPlayer, PlayerOnProgressProps } from '/@/renderer/features/player/audio-player/types';
import { convertToLogVolume } from '/@/renderer/features/player/audio-player/utils/player-utils';
import { logger } from '/@/renderer/utils/logger';
import { PlayerStatus } from '/@/shared/types/types';

export interface WebPlayerEngineHandle extends AudioPlayer {
    player1(): {
        ref: null | ReactPlayer;
        setVolume: (volume: number) => void;
    };
    player2(): {
        ref: null | ReactPlayer;
        setVolume: (volume: number) => void;
    };
}

interface WebPlayerEngineProps {
    isMuted: boolean;
    isTransitioning: boolean;
    loopPlayer1: boolean;
    loopPlayer2: boolean;
    onEndedPlayer1: () => void;
    onEndedPlayer2: () => void;
    onErrorPause: () => void;
    onProgressPlayer1: (e: PlayerOnProgressProps) => void;
    onProgressPlayer2: (e: PlayerOnProgressProps) => void;
    onStartedPlayer1: (player: ReactPlayer) => void;
    onStartedPlayer2: (player: ReactPlayer) => void;
    playerNum: number;
    playerRef: RefObject<null | WebPlayerEngineHandle>;
    playerStatus: PlayerStatus;
    preservesPitch: boolean;
    speed?: number;
    src1: string | undefined;
    src2: string | undefined;
    volume: number;
}

const NETWORK_RETRY_DELAY_MS = 2000;

// Store volume is 0-100; clamp the normalized result so HTMLMediaElement.volume
// never receives a value outside [0, 1] (throws IndexSizeError in some engines)
const normalizeVolume = (volume: number) => {
    return Math.min(1, Math.max(0, volume / 100 || 0));
};

// Credits: https://gist.github.com/novwhisky/8a1a0168b94f3b6abfaa?permalink_comment_id=1551393#gistcomment-1551393
// This is used so that the player will always have an <audio> element. This means that
// player1Source and player2Source are connected BEFORE the user presses play for
// the first time. This workaround is important for Safari, which seems to require the
// source to be connected PRIOR to resuming audio context
const EMPTY_SOURCE =
    'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU2LjM2LjEwMAAAAAAAAAAAAAAA//OEAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAAEAAABIADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV6urq6urq6urq6urq6urq6urq6urq6urq6v////////////////////////////////8AAAAATGF2YzU2LjQxAAAAAAAAAAAAAAAAJAAAAAAAAAAAASDs90hvAAAAAAAAAAAAAAAAAAAA//MUZAAAAAGkAAAAAAAAA0gAAAAATEFN//MUZAMAAAGkAAAAAAAAA0gAAAAARTMu//MUZAYAAAGkAAAAAAAAA0gAAAAAOTku//MUZAkAAAGkAAAAAAAAA0gAAAAANVVV';

export const WebPlayerEngine = (props: WebPlayerEngineProps) => {
    const {
        isMuted,
        isTransitioning,
        loopPlayer1,
        loopPlayer2,
        onEndedPlayer1,
        onEndedPlayer2,
        onErrorPause,
        onProgressPlayer1,
        onProgressPlayer2,
        onStartedPlayer1,
        onStartedPlayer2,
        playerNum,
        playerRef,
        playerStatus,
        preservesPitch,
        speed,
        src1,
        src2,
        volume,
    } = props;

    const player1Ref = useRef<null | ReactPlayer>(null);
    const player2Ref = useRef<null | ReactPlayer>(null);
    const networkRetryCount1 = useRef(0);
    const networkRetryCount2 = useRef(0);
    const [ReactPlayerComponent, setReactPlayerComponent] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const loadReactPlayer = async () => {
            try {
                const module = await import('react-player');
                if (isMounted) {
                    setReactPlayerComponent(() => module.default);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Failed to load react-player:', error);
                setIsLoading(false);
            }
        };

        loadReactPlayer();

        return () => {
            isMounted = false;
        };
    }, []);

    const [internalVolume1, setInternalVolume1] = useState(() => normalizeVolume(volume));
    const [internalVolume2, setInternalVolume2] = useState(() => normalizeVolume(volume));

    useImperativeHandle<WebPlayerEngineHandle, WebPlayerEngineHandle>(playerRef, () => ({
        decreaseVolume(by: number) {
            setInternalVolume1(Math.max(0, internalVolume1 - by / 100));
            setInternalVolume2(Math.max(0, internalVolume2 - by / 100));
        },
        increaseVolume(by: number) {
            setInternalVolume1(Math.min(1, internalVolume1 + by / 100));
            setInternalVolume2(Math.min(1, internalVolume2 + by / 100));
        },
        pause() {
            player1Ref.current?.getInternalPlayer()?.pause();
            player2Ref.current?.getInternalPlayer()?.pause();
        },
        play() {
            player1Ref.current?.getInternalPlayer()?.pause();
            player2Ref.current?.getInternalPlayer()?.pause();
            if (playerNum === 1) {
                player1Ref.current?.getInternalPlayer()?.play();
            } else {
                player2Ref.current?.getInternalPlayer()?.play();
            }
        },
        player1() {
            return {
                ref: player1Ref?.current,
                setVolume: (volume: number) => setInternalVolume1(normalizeVolume(volume)),
            };
        },
        player2() {
            return {
                ref: player2Ref?.current,
                setVolume: (volume: number) => setInternalVolume2(normalizeVolume(volume)),
            };
        },
        seekTo(seekTo: number) {
            let type: 'fraction' | 'seconds' | undefined = undefined;

            if (seekTo < 1) {
                type = 'seconds';
            }

            playerNum === 1
                ? player1Ref.current?.seekTo(seekTo, type)
                : player2Ref.current?.seekTo(seekTo, type);
        },
        setVolume(volume: number) {
            setInternalVolume1(normalizeVolume(volume));
            setInternalVolume2(normalizeVolume(volume));
        },
        setVolume1(volume: number) {
            setInternalVolume1(normalizeVolume(volume));
        },
        setVolume2(volume: number) {
            setInternalVolume2(normalizeVolume(volume));
        },
    }));

    const volume1 = convertToLogVolume(internalVolume1);
    const volume2 = convertToLogVolume(internalVolume2);

    const pauseBothPlayers = useCallback(() => {
        player1Ref.current?.getInternalPlayer()?.pause();
        player2Ref.current?.getInternalPlayer()?.pause();
    }, []);

    const mediaErrorLabel = (code: number | undefined) => {
        switch (code) {
            case MediaError.MEDIA_ERR_ABORTED:
                return 'ABORTED';
            case MediaError.MEDIA_ERR_DECODE:
                return 'DECODE';
            case MediaError.MEDIA_ERR_NETWORK:
                return 'NETWORK';
            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                return 'SRC_NOT_SUPPORTED';
            default:
                return 'unknown';
        }
    };

    // Read at the time a retry fires, not when the error happened.
    const playerStatusRef = useRef(playerStatus);
    const activePlayerNumRef = useRef(playerNum);
    useEffect(() => {
        playerStatusRef.current = playerStatus;
        activePlayerNumRef.current = playerNum;
    }, [playerNum, playerStatus]);
    const isMountedRef = useRef(true);
    const retryTimers = useRef(new Set<ReturnType<typeof setTimeout>>());

    const cancelRetries = useCallback(() => {
        retryTimers.current.forEach((timer) => clearTimeout(timer));
        retryTimers.current.clear();
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            cancelRetries();
        };
    }, [cancelRetries]);

    const handleOnError = (
        slot: number,
        playerRef: React.RefObject<null | ReactPlayer>,
        onEnded: () => void,
        onErrorPause: () => void,
        networkRetryCountRef: React.RefObject<number>,
    ) => {
        return ({ target }: ErrorEvent) => {
            const { current: player } = playerRef;

            // Preloads and the outgoing crossfade slot cannot control the queue.
            if (
                !isMountedRef.current ||
                activePlayerNumRef.current !== slot ||
                !player ||
                !(target instanceof Audio)
            ) {
                return;
            }

            const { error } = target;
            const code = error?.code;
            const label = mediaErrorLabel(code);
            const action = mediaErrorAction({
                code,
                isPlaying: playerStatusRef.current === PlayerStatus.PLAYING,
                retryCount: networkRetryCountRef.current,
            });

            if (action === 'ignore') {
                // A paused element (e.g. a restored queue preloading) is not
                // retried or played; it is loaded again when playback is asked for.
                logger.debug('Playback error while not playing, not retrying', { code, label });
                return;
            }

            if (action === 'retry') {
                networkRetryCountRef.current += 1;
                logger.warn('Playback error, retrying', {
                    code,
                    label,
                    retryCount: networkRetryCountRef.current,
                });
                const audio = target;
                const failedSrc = audio.currentSrc || audio.src;
                const timer = setTimeout(() => {
                    retryTimers.current.delete(timer);
                    if (
                        activePlayerNumRef.current !== slot ||
                        !isRetryStillWanted({
                            currentSrc: audio.currentSrc || audio.src,
                            failedSrc,
                            isMounted: isMountedRef.current,
                            isPlaying: playerStatusRef.current === PlayerStatus.PLAYING,
                        })
                    ) {
                        logger.debug('Playback retry dropped, no longer wanted', { code, label });
                        return;
                    }
                    pauseBothPlayers();
                    audio.load();
                    audio.play().catch(() => {
                        logger.error('Playback retry failed', {
                            code,
                            label,
                            retryCount: networkRetryCountRef.current,
                        });
                    });
                }, NETWORK_RETRY_DELAY_MS);
                retryTimers.current.add(timer);
                return;
            }

            pauseBothPlayers();
            if (action === 'skip') {
                logger.error('Playback decode error, skipping track', {
                    code,
                    label,
                    retryCount: networkRetryCountRef.current,
                });
                onEnded();
            } else {
                logger.error('Playback error, pausing', {
                    code,
                    label,
                    retryCount: networkRetryCountRef.current,
                });
                if (onErrorPause) {
                    onErrorPause();
                }
            }
        };
    };

    // Each slot owns its retry budget. A late preload must not reset the
    // active slot or cancel its timer; the timer's source guard drops stale retries.
    useEffect(() => {
        networkRetryCount1.current = 0;
    }, [src1]);

    useEffect(() => {
        networkRetryCount2.current = 0;
    }, [src2]);

    // An element that failed while paused gets a fresh load once the user asks
    // for playback, instead of staying in its error state.
    useEffect(() => {
        if (playerStatus !== PlayerStatus.PLAYING) {
            cancelRetries();
            return;
        }
        const active = (playerNum === 1 ? player1Ref : player2Ref).current?.getInternalPlayer();
        if (active instanceof HTMLMediaElement && active.error) {
            if (playerNum === 1) networkRetryCount1.current = 0;
            else networkRetryCount2.current = 0;
            active.load();
            active.play().catch(() => {
                logger.warn('Playback could not start after reloading a failed source');
            });
        }
    }, [playerNum, playerStatus, cancelRetries]);

    // When not playing, always pause both players — even during a transition
    useEffect(() => {
        if (playerStatus !== PlayerStatus.PLAYING) {
            pauseBothPlayers();
            return;
        }
        if (isTransitioning) {
            return;
        }
        if (playerNum === 1) {
            player2Ref.current?.getInternalPlayer()?.pause();
        } else {
            player1Ref.current?.getInternalPlayer()?.pause();
        }
    }, [isTransitioning, playerNum, playerStatus, pauseBothPlayers]);

    useEffect(() => {
        const player1 = player1Ref.current?.getInternalPlayer();
        if (player1 && player1 instanceof HTMLAudioElement) {
            player1.preservesPitch = preservesPitch;
        }
        const player2 = player2Ref.current?.getInternalPlayer();
        if (player2 && player2 instanceof HTMLAudioElement) {
            player2.preservesPitch = preservesPitch;
        }
    }, [preservesPitch]);

    const handleOnReadyPlayer1 = useCallback(
        (player: ReactPlayer) => {
            const internal = player.getInternalPlayer();
            if (internal && internal instanceof HTMLAudioElement) {
                internal.preservesPitch = preservesPitch;
            }
            onStartedPlayer1(player);
        },
        [onStartedPlayer1, preservesPitch],
    );

    const handleOnReadyPlayer2 = useCallback(
        (player: ReactPlayer) => {
            const internal = player.getInternalPlayer();
            if (internal && internal instanceof HTMLAudioElement) {
                internal.preservesPitch = preservesPitch;
            }
            onStartedPlayer2(player);
        },
        [onStartedPlayer2, preservesPitch],
    );

    if (isLoading || !ReactPlayerComponent) {
        return <div id="web-player-engine" style={{ display: 'none' }} />;
    }

    return (
        <div id="web-player-engine" style={{ display: 'none' }}>
            <ReactPlayerComponent
                config={{
                    file: { attributes: { crossOrigin: 'anonymous' }, forceAudio: true },
                }}
                controls={false}
                height={0}
                id="web-player-1"
                loop={loopPlayer1}
                muted={isMuted}
                onEnded={src1 && !loopPlayer1 ? () => onEndedPlayer1() : undefined}
                onError={handleOnError(
                    1,
                    player1Ref,
                    () => onEndedPlayer1(),
                    onErrorPause,
                    networkRetryCount1,
                )}
                onProgress={onProgressPlayer1}
                onReady={handleOnReadyPlayer1}
                playbackRate={speed || 1}
                playing={playerNum === 1 && playerStatus === PlayerStatus.PLAYING}
                progressInterval={isTransitioning ? 10 : 250}
                ref={player1Ref}
                url={src1 || EMPTY_SOURCE}
                volume={volume1}
                width={0}
            />
            <ReactPlayerComponent
                config={{
                    file: { attributes: { crossOrigin: 'anonymous' }, forceAudio: true },
                }}
                controls={false}
                height={0}
                id="web-player-2"
                loop={loopPlayer2}
                muted={isMuted}
                onEnded={src2 && !loopPlayer2 ? () => onEndedPlayer2() : undefined}
                onError={handleOnError(
                    2,
                    player2Ref,
                    () => onEndedPlayer2(),
                    onErrorPause,
                    networkRetryCount2,
                )}
                onProgress={onProgressPlayer2}
                onReady={handleOnReadyPlayer2}
                playbackRate={speed || 1}
                playing={playerNum === 2 && playerStatus === PlayerStatus.PLAYING}
                progressInterval={isTransitioning ? 10 : 250}
                ref={player2Ref}
                url={src2 || EMPTY_SOURCE}
                volume={volume2}
                width={0}
            />
        </div>
    );
};

WebPlayerEngine.displayName = 'WebPlayerEngine';
